import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';

/**
 * AnalyticsChart Component
 * Displays a bar chart visualization
 * 
 * @param {Array<number>} barHeights - Array of bar height percentages
 * @param {string} title - Chart title
 */
const AnalyticsChart = ({ barHeights, title }) => {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        borderRadius: borderRadius.lg,
        padding: spacing.sm,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideInUp 0.6s ease-out 0.4s both',
        '@keyframes slideInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          transform: 'translateY(-2px)',
          border: '1px solid rgba(255, 255, 255, 0.35)',
        },
      }}
    >
      <Typography
        sx={{
          fontSize: fonts.fontSizeSm,
          fontWeight: fonts.weightSemiBold,
          color: colors.textWhite,
          mb: spacing.xs,
          textAlign: 'left',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          height: '120px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: '4px',
          padding: '8px 0',
          position: 'relative',
        }}
      >
        {barHeights.map((heightPercent, index) => (
          <Box
            key={index}
            sx={{
              width: '32px',
              height: `${heightPercent}%`,
              minHeight: '25px',
              maxHeight: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              borderRadius: borderRadius.sm,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.4), inset 0 -3px 6px rgba(0, 0, 0, 0.15)',
              background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.08))',
              position: 'relative',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: `growBar${index} 0.8s ease-out ${0.5 + index * 0.08}s both`,
              [`@keyframes growBar${index}`]: {
                from: {
                  height: '0%',
                  opacity: 0,
                  transform: 'scaleY(0)',
                },
                to: {
                  height: `${heightPercent}%`,
                  opacity: 1,
                  transform: 'scaleY(1)',
                },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40%',
                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.3), transparent)',
                borderRadius: `${borderRadius.sm} ${borderRadius.sm} 0 0`,
                pointerEvents: 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '50%',
                background: 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1))',
                borderRadius: `0 0 ${borderRadius.sm} ${borderRadius.sm}`,
                pointerEvents: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(20px) saturate(200%)',
                WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                transform: 'scaleY(1.08) translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.5), inset 0 -3px 6px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                borderTop: '3px solid rgba(255, 255, 255, 0.7)',
                zIndex: 1,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AnalyticsChart;
